import { FC, useEffect, useState } from 'react';
import { Button, Spin, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { isArray, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getMallDecisionReportCompare } from '@/common/api/expandStore/chancepoint';
import { COMPARE_COLORS, COMPARE_FIELDS, COMPARE_MAX_COUNT, COMPARE_RANK_FIELDS, ComparePoint } from '../../compare-config';
import { useCompareValue } from '../../useCompareValue';
import { useAIEvaluation } from './components/AIEvaluation/useAIEvaluation';
import cs from 'classnames';
import styles from './index.module.less';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Empty from '@/common/components/Data/V2Empty';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import IconFont from '@/common/components/IconFont';
import AIEvaluation from './components/AIEvaluation';

interface CompareDrawerProps {
  /** 抽屉是否打开 */
  open: boolean;
  /** 当前报告对应的机会点 ID，作为基准点位且不可删除 */
  chancePointId: number;
  /** 参与对比的机会点 ID 列表，顺序与接口返回列顺序一致 */
  pointIds: number[];
  /** 关闭对比抽屉 */
  onClose: () => void;
  /** 移除某个对比点位 */
  onRemove: (id: number) => void;
  /** 打开对比点位弹窗继续添加 */
  onAdd: () => void;
}

/**
 * @description 多机会点对比抽屉
 * 第一块为对比表格，第二块为前端按 CPM 升序生成的综合数据排序
 */
const CompareDrawer: FC<CompareDrawerProps> = ({
  open,
  chancePointId, // 当前机会点，作为不可删除的基准
  pointIds, // 参与对比的点位 ID，顺序决定列顺序
  onClose,
  onRemove,
  onAdd,
}) => {
  const [compareData, setCompareData] = useState<ComparePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // 对比数据版本，仅在对比接口成功返回后递增，用于触发 AI 评价重新生成
  const [compareVersion, setCompareVersion] = useState(0);
  // 对比表格与综合排序共用的格式化、最优值判定方法
  const compareValueMethods = useCompareValue();
  // 对比组 AI 评价：仅在对比数据版本变化时重新生成，避免每个点位各发一次请求
  const aiEvaluation = useAIEvaluation(compareData, compareVersion);

  const methods = useMethods({
    /**
     * @description 请求多机会点对比列表
     */
    loadCompare() {
      if (!open || pointIds.length < 2) {
        setLoading(false);
        return;
      }
      setLoading(true);
      getMallDecisionReportCompare({
        pointIds: pointIds.map((id) => Number(id)),
      }).then((result: any) => {
        setCompareData(isArray(result) ? result : []);
        // 先更新数据再递增版本，保证 AI 评价读到的 points 是本次最新对比组
        setCompareVersion((current) => current + 1);
        setFailed(false);
      }, (error: any) => {
        console.log(error);
        setCompareData([]);
        setFailed(true);
      }).finally(() => {
        setLoading(false);
      });
    },
    /**
     * @description 删除某个对比点位
     * 基准点位不可删除，且至少保留 2 个点位用于对比
     */
    removePoint(id: number) {
      if (Number(id) === Number(chancePointId)) {
        V2Message.info('基准点位不可删除');
        return;
      }
      if (pointIds.length <= 2) {
        V2Message.info('至少需要保留2个对比点位');
        return;
      }
      // 先本地移除保证操作即时反馈，父级更新点位后会自动重新请求对比接口
      setCompareData((current) => current.filter((point) => Number(point.pointId) !== Number(id)));
      onRemove(Number(id));
    },
    /**
     * @description 返回指定点位的删除事件，避免在 JSX 中直接写匿名函数
     */
    getRemoveHandler(id: number) {
      return () => methods.removePoint(id);
    },
    /**
     * @description 打开对比点位弹窗
     */
    openAdd() {
      onAdd();
    },
    /**
     * @description 关闭对比抽屉
     */
    handleClose() {
      onClose();
    },
    /**
     * @description 综合数据排序：按 CPM 升序，缺失值排到最后
     */
    sortByCpm(points: ComparePoint[]) {
      const getCpm = (value: any) => {
        if (!isNotEmpty(value)) return Number.POSITIVE_INFINITY;
        const current = Number(value);
        return Number.isNaN(current) ? Number.POSITIVE_INFINITY : current;
      };
      return [...points].sort((prev, next) => getCpm(prev.cpmMS) - getCpm(next.cpmMS));
    },
    /**
     * @description 排序卡片副标题：商场名 · 楼层 · 面积
     */
    getRankMeta(point: ComparePoint) {
      const areaValue = Number(point.builtArea);
      const area = isNotEmpty(point.builtArea) && !Number.isNaN(areaValue) ? `${String(areaValue)}㎡` : '';
      return [point.modelClusterName, point.groundFloor, area].filter(Boolean).join(' · ') || '-';
    },
  });

  useEffect(() => {
    methods.loadCompare();
  }, [open, pointIds]);

  return (
    <V2Drawer
      open={open}
      title={<div className='pt-24 pb-24 pl-40 fs-20 fw-600'>点位对比</div>}
      onClose={methods.handleClose}
      bodyStyle={{ padding: 0 }}
      contentWrapperStyle={{ width: '1100px' }}
      className={styles.compareDrawer}
    >
      <Spin spinning={loading}>
        <div className={styles.compareBody}>
          {failed && !compareData.length ? (
            <div className={styles.failedState}>
              <V2Empty
                type='fail'
                customTip='对比数据加载失败'
              />
              <Button onClick={methods.loadCompare}>重试</Button>
            </div>
          ) : compareData.length ? (
            <>
              <section className={styles.compareSection}>
                <div className={styles.compareColWrap}>
                  <div className={styles.leftCol}>
                    <div className={styles.leftHeader}>
                      {pointIds.length >= COMPARE_MAX_COUNT ? (
                        <span className={styles.pkText}>PK对比</span>
                      ) : (
                        <div
                          className={styles.addCompare}
                          onClick={methods.openAdd}
                        >
                          <IconFont
                            iconHref='iconic_add_xiangqing'
                            className={styles.addIcon}
                          />
                          添加对比
                        </div>
                      )}
                    </div>
                    {COMPARE_FIELDS.map((field) => (
                      <div
                        className={styles.listCol}
                        key={field.key}
                      >
                        <span className={styles.fieldLabel}>{field.label}</span>
                        {field.description && (
                          <Tooltip
                            title={field.description}
                            placement='topLeft'
                          >
                            <QuestionCircleOutlined className={styles.fieldTip} />
                          </Tooltip>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.rightCol}>
                    {compareData.map((point, index) => (
                      <div
                        className={styles.rightColItem}
                        key={point.pointId}
                      >
                        <div
                          className={styles.itemHeader}
                          style={{ boxShadow: `inset 0 4px 0 0 ${COMPARE_COLORS[index % COMPARE_COLORS.length]}` }}
                        >
                          <div className={styles.itemHeaderContent}>
                            <div className={styles.headerWrap}>
                              <span className={styles.headerName}>
                                <Typography.Text ellipsis={{ tooltip: isNotEmpty(point.name) ? point.name : '-' }}>
                                  {isNotEmpty(point.name) ? point.name : '-'}
                                </Typography.Text>
                              </span>
                              {Number(point.pointId) === Number(chancePointId) && (
                                <span className={styles.baseTag}>基准</span>
                              )}
                            </div>
                            {isNotEmpty(point.modelClusterName) && (
                              <div className={styles.headerShop}>
                                <Typography.Text ellipsis={{ tooltip: point.modelClusterName }}>
                                  {point.modelClusterName}
                                </Typography.Text>
                              </div>
                            )}
                          </div>
                          {Number(point.pointId) !== Number(chancePointId) && (
                            <IconFont
                              iconHref='icondelete'
                              className={styles.deleteIcon}
                              onClick={methods.getRemoveHandler(point.pointId)}
                            />
                          )}
                        </div>
                        {COMPARE_FIELDS.map((field) => {
                          const value = point[field.key];
                          const isBest = compareValueMethods.isBestValue(compareData, field.key, value, field.best);
                          const formattedValue = compareValueMethods.formatValue(value, field);
                          return (
                            <div
                              className={styles.listCol}
                              key={field.key}
                            >
                              {field.kind === 'text' ? (
                                <Typography.Text
                                  className={cs(styles.textValue, isBest && styles.bestValue)}
                                  ellipsis={{ tooltip: formattedValue }}
                                >
                                  {formattedValue}
                                </Typography.Text>
                              ) : (
                                <span
                                  className={cs(styles.valueText, isBest && styles.bestValue)}
                                  title={formattedValue}
                                >
                                  {formattedValue}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
              <section className={styles.rankSection}>
                <div className={styles.rankTitleRow}>
                  <span className={styles.rankTitle}>综合数据排序</span>
                  <span className={styles.rankRule}>
                    排序规则：优先按 CPM（每千人次过店客流成本）升序排序；每个点位展示关键指标与 AI 评价，供审批人快速判断
                  </span>
                </div>
                {methods.sortByCpm(compareData).map((point, index) => (
                  <div
                    className={styles.rankItem}
                    key={point.pointId}
                  >
                    <div className={styles.rankBody}>
                      <div className={styles.rankMain}>
                        <div className={styles.rankHeader}>
                          <span className={styles.rankBadge}>第{index + 1}名</span>
                          <span
                            className={styles.rankName}
                            title={isNotEmpty(point.name) ? point.name : '-'}
                          >
                            {isNotEmpty(point.name) ? point.name : '-'}
                          </span>
                          <span className={styles.rankMeta}>{methods.getRankMeta(point)}</span>
                        </div>
                        <div className={styles.rankMetrics}>
                          {COMPARE_RANK_FIELDS.map((field) => (
                            <div
                              className={styles.rankMetric}
                              key={field.key}
                            >
                              <strong>{compareValueMethods.formatRankValue(point[field.key], field)}</strong>
                              <span>{field.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <AIEvaluation
                        comment={aiEvaluation.comments[String(point.pointId)]}
                        loading={aiEvaluation.loading}
                        failed={aiEvaluation.failed}
                      />
                    </div>
                  </div>
                ))}
              </section>
            </>
          ) : loading ? null : <V2Empty />}
        </div>
      </Spin>
    </V2Drawer>
  );
};

export default CompareDrawer;
